/**
 * Application Portfolio Heatmap Widget (issue #1652, part of epic #1650)
 *
 * Shows a matrix of service counts by division x lifecycle status, matching
 * `homepage-dashboard-spec.md`'s "Application Portfolio Heatmap" widget.
 * Rows are the caller's resolved division(s) — walking `parentTeamId` to
 * the hierarchy root (ADR-1254), never a `Team.division` field.
 * Administrators see one row per division present in the portfolio; other
 * users see only their own division's row(s).
 */

import { usePortfolioHeatmap, type PortfolioHeatmapRow } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './PortfolioHeatmapWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'portfolio-heatmap',
  title: 'Application Portfolio Heatmap',
  size: 'large',
  refreshInterval: 600000, // 10 minutes — matches the sibling division-scoped widgets' cadence
  showRefresh: true,
  showExpand: false,
};

interface PortfolioHeatmapStats {
  matrix: PortfolioHeatmapRow[];
}

type PortfolioHeatmapQueryData = ReturnType<typeof usePortfolioHeatmap>['data'];

function extractPortfolioHeatmapStats(
  data: PortfolioHeatmapQueryData
): PortfolioHeatmapStats | null {
  if (!data?.portfolioHeatmap) {
    return null;
  }
  return data.portfolioHeatmap;
}

/** Cell intensity class, scaled off the row's own total so each division's
 * heatmap reads relative to itself rather than the whole matrix's max. */
function intensityClass(count: number, rowTotal: number): string {
  if (rowTotal === 0 || count === 0) return 'portfolio-heatmap-widget__cell--none';
  const ratio = count / rowTotal;
  if (ratio >= 0.66) return 'portfolio-heatmap-widget__cell--high';
  if (ratio >= 0.33) return 'portfolio-heatmap-widget__cell--medium';
  return 'portfolio-heatmap-widget__cell--low';
}

export function PortfolioHeatmapWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    usePortfolioHeatmap(),
    extractPortfolioHeatmapStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats || stats.matrix.length === 0) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="portfolio-heatmap-widget__empty">
          <IconElement name="grid" className="portfolio-heatmap-widget__empty-icon" />
          <p className="forge-typography--body2">No portfolio data available</p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <div className="portfolio-heatmap-widget__matrix" data-testid="portfolio-heatmap-matrix">
        <div className="portfolio-heatmap-widget__row portfolio-heatmap-widget__row--header">
          <span>Division</span>
          <span>Production</span>
          <span>Development</span>
          <span>Deprecated</span>
          <span>Total</span>
        </div>
        {stats.matrix.map((row) => (
          <div
            key={row.businessUnit}
            className="portfolio-heatmap-widget__row"
            data-testid="portfolio-heatmap-row"
          >
            <span className="portfolio-heatmap-widget__row-label">{row.businessUnit}</span>
            <span className={intensityClass(row.production, row.total)}>{row.production}</span>
            <span className={intensityClass(row.development, row.total)}>{row.development}</span>
            <span className={intensityClass(row.deprecated, row.total)}>{row.deprecated}</span>
            <span className="portfolio-heatmap-widget__total-cell">{row.total}</span>
          </div>
        ))}
      </div>
    </DashboardWidget>
  );
}
